import React, { useState } from "react";
import {
  BottomSheet,
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  useEndReached,
} from "../../../components/ui";
import { formatCreatedOn, formatPrice } from "../../brand/planFormat";
import {
  PAYMENT_STATUS_PILL,
  PAYMENT_STATUS_STYLES,
} from "../paymentStatusStyles";
import type { RevenuePayment } from "../types/revenue.types";

/** The height the cards stand in at while the list is on its way — about three
 *  cards, which is what a phone shows before the fold. */
const CARDS_SKELETON = "h-[300px]";

const formatDate = (iso: string): string => formatCreatedOn(new Date(iso));

/** One label-and-value line in the sheet. The label is fixed-width so the
 *  values line up down the sheet rather than each starting wherever its own
 *  label happened to end. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline gap-3 py-2.5">
    <dt className="w-[104px] flex-shrink-0 text-[13px] text-ink-muted">
      {label}
    </dt>
    <dd className="min-w-0 flex-1 text-[14px] text-ink">{children}</dd>
  </div>
);

interface PaymentCardsProps {
  payments: RevenuePayment[];
  /** Stands in for the cards when the list comes back with none. */
  empty: EmptyStateProps;
  /** Where the list is — the same contract `RevenueTable` works to. */
  phase?: LoadPhase;
  /** Whether the caller is holding rows past the ones it has handed over. */
  hasMore?: boolean;
  /** Whether the window itself is still coming in from the server. */
  loadingMore?: boolean;
  /** Asks for the next batch — see `useEndReached`. */
  onEndReached?: () => void;
}

/**
 * The payments list as it reads on a phone: one card per payment instead of a
 * table, and no card around the lot of them.
 *
 * The table needs 980px to lay its nine columns out. On a phone that is either
 * clipped at the right edge or scrolled sideways inside a card that is itself
 * inside a page — neither of which is a list. So the row becomes a card of its
 * own on the page's own background, carrying what the reader is scanning for:
 * who was asked, what for, how much, and where it stands.
 *
 * The other five fields are a press away in a bottom sheet rather than crammed
 * onto the card. A card that holds all nine is a table row again, set sideways
 * — and the four on the face of it are what anybody is actually running their
 * eye down a ledger for.
 *
 * Which of the two renders is `RevenuePage`'s decision — see `useIsMobile`.
 */
const PaymentCards: React.FC<PaymentCardsProps> = ({
  payments,
  empty,
  phase = "ready",
  hasMore = false,
  loadingMore = false,
  onEndReached,
}) => {
  // Which payment is open in the sheet, if any. The payment itself rather than
  // its id: the sheet reads it whole, and a row that has since been reloaded
  // out from under it would leave an id pointing at nothing.
  const [open, setOpen] = useState<RevenuePayment | null>(null);

  const { ref, onScroll } = useEndReached<HTMLDivElement>(
    hasMore,
    onEndReached
  );

  if (phase !== "ready" && payments.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (payments.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in — centred as a row it would be sized by its longest word and
    // could push past a phone's edge. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <>
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay"
      >
        <ul className="flex flex-col gap-3 pb-4">
          {payments.map((payment) => (
            <li key={payment.id}>
              <button
                type="button"
                onClick={() => setOpen(payment)}
                aria-haspopup="dialog"
                // The same corner the three status tiles above the list wear —
                // they are the only other card on this screen, and two radii a
                // thumb's width apart read as two different surfaces.
                className="w-full card-surface border border-line bg-white p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  {/* `min-w-0` is what lets a long name shorten instead of
                      pushing the pill off the card's edge. */}
                  <div className="min-w-0">
                    <h3 className="truncate text-[15px] font-medium text-ink">
                      {payment.clientName}
                    </h3>
                    {/* What was sold, and only that: the card is a line of the
                        ledger, and the service is what the amount under it is
                        for. Where the client lives and what they are working
                        towards are facts about them rather than about the
                        payment — both wait in the sheet with the rest of the
                        record, and neither is worth a plan name cut short
                        to fit beside it. */}
                    <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                      {payment.planName}
                    </p>
                  </div>
                  <span
                    className={`${PAYMENT_STATUS_PILL} flex-shrink-0 ${
                      PAYMENT_STATUS_STYLES[payment.status]
                    }`}
                  >
                    {payment.status}
                  </span>
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-line pt-3">
                  <span className="text-[16px] font-semibold text-ink">
                    {formatPrice(payment.amount)}
                  </span>
                  <span className="text-[12px] text-ink-subtle">
                    {formatDate(payment.generatedAt)}
                  </span>
                </div>
              </button>
            </li>
          ))}

          {loadingMore && (
            <li className="py-3 text-center text-[13px] text-ink-subtle">
              Loading more payments…
            </li>
          )}
        </ul>
      </div>

      {/* The rest of the record. Titled by who was asked, since that is what
          was pressed to get here. */}
      <BottomSheet
        open={!!open}
        title={open?.clientName ?? ""}
        onClose={() => setOpen(null)}
      >
        {open && (
          <dl className="divide-y divide-[#F1F4F6] px-5 pb-2">
            <Field label="Amount">{formatPrice(open.amount)}</Field>
            <Field label="Status">
              <span
                className={`${PAYMENT_STATUS_PILL} ${
                  PAYMENT_STATUS_STYLES[open.status]
                }`}
              >
                {open.status}
              </span>
            </Field>
            <Field label="Place">{open.place}</Field>
            <Field label="Goal">{open.goal}</Field>
            <Field label="Plan">{open.planName}</Field>
            <Field label="Duration">{open.duration}</Field>
            <Field label="Generated on">{formatDate(open.generatedAt)}</Field>
            {/* Anything not paid has no date to show, and leaving it blank
                would read as one lost. */}
            <Field label="Paid on">
              {open.paidAt ? formatDate(open.paidAt) : "NA"}
            </Field>
          </dl>
        )}
      </BottomSheet>
    </>
  );
};

export default PaymentCards;
